<svelte:options runes={true} />

<script lang="ts">
  import NavMenuItem from "~/components/ui/NavMenuItem/NavMenuItem.svelte";
  import type { AutosuggestItem } from "~/lib/search/create-search-autosuggest-core.svelte";

  interface Props {
    items?: Extract<AutosuggestItem, { kind: "suggestion" }>[];
    highlightedItemId?: string | undefined;
    onSelectTerm?: (term: string) => void;
    /** Loading state — same heading, rule and row box as the real list. */
    skeleton?: boolean;
    class?: string;
  }

  let {
    items = [],
    highlightedItemId,
    onSelectTerm,
    skeleton = false,
    class: className = "",
  }: Props = $props();

  const SKELETON_ROWS = [0, 1, 2, 3, 4];
</script>

{#if skeleton}
  <div class={["da-popular-search-list", className]} aria-hidden="true">
    <p class="da-popular-search-list__title">Popular Searches</p>
    {#each SKELETON_ROWS as i (i)}
      <div class="da-popular-search-list__skeleton-row">
        <span class="da-skeleton"></span>
      </div>
    {/each}
  </div>
{:else if items.length > 0}
  <div class={["da-popular-search-list", className]} role="group" aria-label="Popular searches">
    <!-- The group's aria-label carries the accessible name; this heading is decorative
         so the listbox contains options only (aria-required-children). -->
    <p class="da-popular-search-list__title" aria-hidden="true">Popular Searches</p>
    {#each items as item (item.id)}
      <NavMenuItem
        label={item.term}
        optionId={item.id}
        selected={item.id === highlightedItemId}
        onclick={() => onSelectTerm?.(item.term)}
      />
    {/each}
  </div>
{/if}

<style>
  @layer aurora {
    .da-popular-search-list {
      display: flex;
      flex-direction: column;
      /* No gap: NavMenuItem's own padding is the row rhythm — a gap on top of it
         double-spaces the list. */
      gap: 0;
      width: 100%;
    }

    /* Figma 9980:1580 carries a 2px bottom border in sand-500 — the panel's own
       background — which the designer confirmed (2026-09-01) was accidental rather
       than an invisible-by-intent divider. There is no rule under this heading; the
       separation is the 12px of padding alone. An earlier pass had recoloured that
       border to sand-900 to make it show, which is why one briefly rendered. */
    .da-popular-search-list__title {
      margin: 0;
      padding: 0 var(--da-space-3) var(--da-space-3);
      color: var(--da-color-charcoal-500);
      font-family: var(--da-font-family-primary);
      font-weight: 600;
      font-size: var(--da-font-size-md);
      /* 24px per the design; the nearest token is --da-line-height-md at 22px. */
      line-height: 24px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    /* Matches NavMenuItem's box, so skeleton rows do not reflow into real rows. */
    .da-popular-search-list__skeleton-row {
      padding: 6px var(--da-space-3);
    }

    .da-popular-search-list__skeleton-row .da-skeleton {
      display: block;
      /* Matches NavMenuItem's line box, so rows do not reflow when results land. */
      height: 24px;
    }
  }
</style>
